import { z } from "zod"

export const PRODUCT_VISUAL_PIM_COMPLETION_JOB_ID = "product-visual-pim-completion"
export const PRODUCT_VISUAL_PIM_COMPLETION_FAILURE_LIMIT = 100

const count = z.number().int().nonnegative()
const countsSchema = z
  .object({
    eligible: count,
    validPim: count,
    sourceGaps: count,
    denominator: count,
    current: count,
    new: count,
    stale: count,
    excluded: count,
    actionable: count,
    paused: count,
    retryDue: count,
    failedExhausted: count,
    unresolved: count,
    // Absent on certificates sealed before policy-history classification existed.
    policyHistory: count.optional(),
    contentAgreement: count,
    templateAgreement: count,
    outputAgreement: count,
    fingerprintAgreement: count,
    gcsRequired: count,
    gcsRead: count,
    gcsVerified: count,
  })
  .strict()

export const PRODUCT_VISUAL_PIM_COMPLETION_FAILURE = {
  sourceUnavailable: "source_unavailable",
  sourcePartial: "source_partial",
  ownerMissing: "owner_missing",
  sourceChanged: "source_changed",
  sourceStale: "source_stale",
  auditIncomplete: "audit_incomplete",
  revisionMissing: "revision_missing",
  templateUnreleased: "template_unreleased",
  notCurrent: "not_current",
  unresolvedWork: "unresolved_work",
  gcsMissing: "gcs_missing",
  gcsHashMismatch: "gcs_hash_mismatch",
  gcsUnreadable: "gcs_unreadable",
  leaseLost: "lease_lost",
} as const

export const productVisualPimCompletionOutcomeSchema = z
  .object({
    version: z.literal(1),
    kind: z.literal("product_visual_pim_completion"),
    verdict: z.enum(["pass", "fail"]),
    completed: z.boolean(),
    startedAt: z.string().datetime(),
    finishedAt: z.string().datetime(),
    revision: z.string().max(200).nullable(),
    controls: z
      .object({
        pimNutritionEnabled: z.boolean(),
        // Historical certificates predate the independent Magento dispatch control.
        magentoNutritionEnabled: z.boolean().optional(),
        batchingEnabled: z.boolean(),
        auditEnabled: z.boolean(),
        eventsEnabled: z.boolean(),
      })
      .strict(),
    sourceBoundary: z
      .object({
        kind: z.literal("authoritative_pim_locale_owner"),
        uniqueLocaleProducts: count,
        requestedProducts: count,
        responseRows: count,
        complete: z.boolean(),
        firstReadAt: z.string().datetime().nullable(),
        lastReadAt: z.string().datetime().nullable(),
        maxReadAgeMs: count,
        initialEvidenceHash: z.string().length(64),
        finalEvidenceHash: z.string().length(64),
        denominatorHash: z.string().length(64),
      })
      .strict(),
    sourceGaps: z.object({ absent: count, invalid: count, unknown: count }).strict(),
    counts: countsSchema,
    locales: z
      .array(z.object({ locale: z.string().max(20), counts: countsSchema }).strict())
      .max(100),
    stores: z
      .array(
        z
          .object({ store: z.string().max(100), locale: z.string().max(20), counts: countsSchema })
          .strict()
      )
      .max(100),
    latestAudit: z
      .object({
        runId: z.string().max(200).nullable(),
        finishedAt: z.string().datetime().nullable(),
        completed: z.boolean(),
        uniqueLocaleProducts: count,
        compared: count,
        unexplainedUnbaselined: count.nullable(),
      })
      .strict(),
    failureCount: count,
    failures: z
      .array(
        z
          .object({
            reason: z.enum(PRODUCT_VISUAL_PIM_COMPLETION_FAILURE),
            store: z.string().max(100).nullable(),
            locale: z.string().max(20).nullable(),
            simpleSku: z.string().max(256).nullable(),
            magentoId: z.number().int().positive().nullable(),
          })
          .strict()
      )
      .max(PRODUCT_VISUAL_PIM_COMPLETION_FAILURE_LIMIT),
    failuresTruncated: z.boolean(),
  })
  .strict()

export type ProductVisualPimCompletionOutcome = z.infer<
  typeof productVisualPimCompletionOutcomeSchema
>
export type ProductVisualPimCompletionCounts = ProductVisualPimCompletionOutcome["counts"]
